import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { api, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const STATUS_STYLES = {
  idle: { color: "text-white/50", label: "Idle" },
  running: { color: "text-emerald-400", label: "Running" },
  stopping: { color: "text-amber-400", label: "Stopping" },
  paused: { color: "text-amber-400", label: "Paused" },
  done: { color: "text-emerald-400", label: "Complete" },
  error: { color: "text-red-400", label: "Error" },
};

export default function SyncWidget() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/youtube/sync/status");
      setState(res.data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const isActive = state?.status === "running" || state?.status === "stopping";
    const t = setInterval(load, isActive ? 2000 : 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.status]);

  const start = async () => {
    setBusy(true);
    try {
      const res = await api.post("/youtube/sync/full", { resume: true });
      if (res.data?.ok) {
        toast.success(res.data.resume ? "Resuming previous sync…" : "Full sync started");
      } else {
        toast.error(res.data?.message || "Could not start sync");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start sync");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/youtube/sync/stop");
      toast.message("Stop signal sent");
      await load();
    } catch (e) {
      toast.error("Failed to stop sync");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const status = state.status || "idle";
  const meta = STATUS_STYLES[status] || STATUS_STYLES.idle;
  const target = state.target || 0;
  const inDb = state.videos_in_db || 0;
  const displayTarget = Math.max(target, inDb);
  const pct = displayTarget > 0 ? Math.min(100, Math.round((inDb / displayTarget) * 100)) : 0;
  const isRunning = status === "running";
  const isStopping = status === "stopping";

  const Icon =
    status === "running"
      ? Loader2
      : status === "done"
      ? CheckCircle2
      : status === "error"
      ? AlertCircle
      : status === "paused" || status === "stopping"
      ? Pause
      : RefreshCw;

  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#12141d] p-6"
      data-testid="sync-widget"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="mono-label">Vault Sync</div>
          <div className="mt-2 flex items-center gap-2">
            <Icon
              className={`w-4 h-4 ${meta.color} ${isRunning ? "animate-spin" : ""}`}
            />
            <span
              data-testid="sync-status-label"
              className={`font-mono-tech text-xs uppercase tracking-[0.2em] ${meta.color}`}
            >
              {meta.label}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isRunning && !isStopping && (
            <Button
              onClick={start}
              disabled={busy}
              data-testid="sync-start-btn"
              className="rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.2em]"
            >
              <Play className="w-3.5 h-3.5 mr-2" />
              {status === "paused" || status === "error" ? "Resume sync" : "Sync full channel"}
            </Button>
          )}
          {(isRunning || isStopping) && (
            <Button
              onClick={stop}
              disabled={busy || isStopping}
              data-testid="sync-stop-btn"
              variant="outline"
              className="rounded-full border-white/20 text-white hover:text-[#d95d39] hover:border-[#d95d39] font-mono-tech text-[11px] uppercase tracking-[0.2em] bg-transparent"
            >
              <Pause className="w-3.5 h-3.5 mr-2" /> Stop
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-baseline justify-between font-mono-tech text-[11px] uppercase tracking-widest text-white/40">
          <span data-testid="sync-progress-label">
            {formatNumber(inDb)} / {displayTarget ? formatNumber(displayTarget) : "?"} archived
          </span>
          <span data-testid="sync-progress-pct">{pct}%</span>
        </div>
        <Progress
          value={pct}
          className="h-2 mt-2 bg-white/5 [&>*]:bg-[#d95d39]"
        />
      </div>

      {state.message && (
        <div
          className="mt-4 font-mono-tech text-[11px] uppercase tracking-widest text-white/50"
          data-testid="sync-message"
        >
          {state.message}
        </div>
      )}

      <div className="mt-5 grid grid-cols-3 gap-px bg-white/10 rounded-xl overflow-hidden">
        {[
          { l: "Added", v: state.added || 0 },
          { l: "Updated", v: state.updated || 0 },
          { l: "In DB", v: inDb },
        ].map((s) => (
          <div key={s.l} className="bg-[#0c0e15] p-3 text-center">
            <div className="font-serif-editorial text-lg text-[#f4f1ea]">
              {formatNumber(s.v)}
            </div>
            <div className="mono-label text-white/40 mt-1">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
